import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { DEFAULT_CASE_SIZE, rank, recommend } from '@theme/club-scoring';
import {
  SIZES, price, resize, colourSplit, colourKey, profileKey, lines, candidates, swap, difference, isShowstopper,
  reason, fitting, compareSizes, paceDefaults, totals, profileName, foodName, openingSize, answersKey,
  wholePounds, restoreCase, byBand, bandStep, swapCount, landing,
} from '@theme/club-case';
import { createAdapter } from '@theme/club-bundle-adapter';
import { track, trackThen, entryFrom, moneyFields } from '@theme/club-events';
import { readCase, writeCase, clearAll } from '@theme/club-case-store';

/**
 * The wine club funnel after the quiz: reveal, then result (sections/club-result.liquid).
 *
 * Renders from the quiz's `wine-club:complete` event, never from its own payload, so the page
 * carries one pool. Orders go through the bundle adapter, the only file that knows Loop exists.
 * On the stub route nothing is placed, and the notice says so. On the api route the case goes
 * into the cart and the customer follows it there.
 *
 * This file lays figures out and computes none. Why a bottle is in the case, how many wines fit
 * the answers, what twelve saves over six and what is charged today all come from
 * assets/club-case.js, which Node tests hold (wine-club/SPEC-build-your-case-takeover.md on
 * project-docs, sections 3.4 to 3.7).
 *
 * THE BAR ABOVE IS THE QUIZ'S. This file never touches it. It sends `wine-club:view` on the
 * document whenever the screen it shows changes ("reveal" or "case"), and the quiz labels its bar
 * from that. It listens for `wine-club:back`, which the bar's Back sends: from the case that is
 * the reveal again, from the reveal it is nothing of this section's, so it hides and the quiz
 * puts its last question back. "Change my answers" still sends `wine-club:restart`.
 *
 * THE COLOUR AND PRICE FACTS CHANGE ONE ANSWER. On the reveal they are buttons: pressing one hides
 * this section and sends `wine-club:edit` with the axis, and the quiz opens that question with every
 * other answer kept and comes straight back here when the visitor moves on, with a fresh
 * wine-club:complete. They are buttons only when the quiz has that question (`detail.axes` on
 * wine-club:complete); otherwise each shows its plain twin. The wine count is only ever a fact.
 *
 * START AGAIN IS NOT CHANGE MY ANSWERS. Each screen has a Start again at its top and after its
 * case. It sends `wine-club:start-again`, and the quiz forgets the answers and the saved case, so
 * the sitewide pick-up (assets/club-pickup.js) has nothing to offer, and opens question 1 with
 * nothing ticked. "Change my answers" keeps them. This section forgets the case it built too, so
 * the same answers given again build a fresh case rather than reopening the old one. Neither
 * touches the cart.
 *
 * ONE H1 AT A TIME. Both screens' headings are h2 in the markup, under the quiz's h1, which is
 * what the server renders and what gate:heading-structural reads. While a screen is up the quiz's
 * headings are all hidden, so #setView raises that screen's heading to level 1 with aria-level,
 * as the quiz does for the question on screen, and lowers the other.
 *
 * The quiz may say which case size and frequency to open on (`detail.size`, `detail.cadence`,
 * from its "how much do you get through" question). When it does not, the case opens as it
 * always has: six bottles, at Loop's default frequency, and the line about fitting what they
 * told us stays hidden because they told us nothing. When the pool cannot fill the size it asked
 * for, the case opens on the size it can (club-case.js `openingSize`), the reveal says so, and the
 * other size's face is switched off with the reason on it.
 *
 * NOTHING IS SWITCHED OFF WITHOUT A REASON BESIDE IT. The order buttons are off while a case is
 * short and while one is on its way to the cart. The first has a line by the buttons and in the
 * pinned bar, which each button is pointed at; the second changes the pressed button's words.
 *
 * A FAILED ORDER IS TAKEN TO THE CUSTOMER. The notice is by the panel's buttons, over a screen
 * away from the pinned bar on a phone, so it takes focus and is scrolled to.
 *
 * THE CASE IS SAVED, and comes back (assets/club-case-store.js). Every time it is built or
 * changes (a swap, the size, the frequency) its bottles, size and frequency are written with the
 * answers they were built from. When the quiz completes with answers a saved case was built from,
 * that case is put back instead of a new one: the same bottles in the same slots, the size and
 * the frequency. A saved bottle that has left the club or sold out since is replaced as a swap
 * would replace it (club-case.js `restoreCase`), and one line on the case names the wine that went
 * and the one that took its place. Once a case is in the cart, Subscribe or Buy once, the saved
 * case and the answers are forgotten, so nobody who has bought is asked to pick it up.
 *
 * WHICH SCREEN. The quiz says which one to open on (`detail.view` on wine-club:complete): the
 * reveal, or the case when a saved case is picked up or the browser's Forward lands on it. It sends
 * wine-club:hide when Back or Forward goes from here to one of its own screens.
 *
 * ABOUT A BOTTLE. A card's picture and name are one button that opens a sheet about the wine: the
 * same native dialog as the swap drawer, filled from sections/club-wine-details.liquid through the
 * Section Rendering API when it opens, so the product page's own snippets and blocks draw it and
 * nothing is loaded for the 103 wines nobody asks about. A sheet once fetched is kept for the
 * visit, so opening it again costs nothing.
 *
 * @typedef {import('./club-case.js').Wine} Wine
 * @typedef {import('./club-case.js').Answers} Answers
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} profiles
 * @property {HTMLElement} reveal
 * @property {HTMLElement} profileName
 * @property {HTMLElement} why
 * @property {HTMLElement} fan
 * @property {HTMLElement} factSplit
 * @property {HTMLElement} factPrice
 * @property {HTMLElement} factSwaps
 * @property {HTMLElement} widened
 * @property {HTMLElement} widenedLead
 * @property {HTMLUListElement} named
 * @property {HTMLTemplateElement} namedTemplate
 * @property {HTMLButtonElement} seeCase
 * @property {HTMLButtonElement} revealRestart
 * @property {HTMLElement} resultView
 * @property {HTMLElement} resultEyebrow
 * @property {HTMLElement} resultHeading
 * @property {HTMLElement} resultIntro
 * @property {HTMLElement} shortNote
 * @property {HTMLUListElement} caseList
 * @property {HTMLButtonElement} restart
 * @property {HTMLButtonElement} shortRestart
 * @property {HTMLButtonElement[]} startAgain
 * @property {HTMLInputElement[]} sizeInputs
 * @property {HTMLElement[]} sizeSaves
 * @property {HTMLElement[]} sizeRates
 * @property {HTMLElement[]} eachPrices
 * @property {HTMLElement} compare
 * @property {HTMLElement} compareLead
 * @property {HTMLElement} compareRest
 * @property {HTMLInputElement[]} cadenceInputs
 * @property {HTMLElement} fit
 * @property {HTMLElement} total
 * @property {HTMLElement} panel
 * @property {HTMLElement} pay
 * @property {HTMLElement} due
 * @property {HTMLElement} offReason
 * @property {HTMLElement} cut
 * @property {HTMLElement[]} sizeWhys
 * @property {HTMLElement} nextTodayRow
 * @property {HTMLElement} nextToday
 * @property {HTMLElement} nextEvery
 * @property {HTMLButtonElement} subscribe
 * @property {HTMLButtonElement} buyOnce
 * @property {HTMLElement} notice
 * @property {HTMLElement} sticky
 * @property {HTMLElement} stickyPrice
 * @property {HTMLElement} stickyCadence
 * @property {HTMLElement} stickyDetail
 * @property {HTMLButtonElement} stickySubscribe
 * @property {HTMLElement} announce
 * @property {HTMLTemplateElement} bottleTemplate
 * @property {HTMLDialogElement} drawer
 * @property {HTMLElement} drawerHeading
 * @property {HTMLElement} drawerCurrent
 * @property {HTMLButtonElement} drawerClose
 * @property {HTMLButtonElement} colourChip
 * @property {HTMLButtonElement} bodyChip
 * @property {HTMLSelectElement} countryFilter
 * @property {HTMLSelectElement} priceFilter
 * @property {HTMLUListElement} candidateList
 * @property {HTMLElement} drawerEmpty
 * @property {HTMLElement} drawerNote
 * @property {HTMLTemplateElement} candidateTemplate
 * @property {HTMLElement} replacedNote
 * @property {HTMLDialogElement} about
 * @property {HTMLElement} aboutHeading
 * @property {HTMLButtonElement} aboutClose
 * @property {HTMLElement} aboutStatus
 * @property {HTMLElement} aboutBody
 * @property {HTMLElement} aboutError
 * @property {HTMLButtonElement} aboutRetry
 * @property {HTMLAnchorElement} aboutErrorLink
 * @property {HTMLButtonElement} aboutSwap
 * @property {HTMLAnchorElement} aboutLink
 *
 * @extends {Component<Refs>}
 */
/** The pool's colour labels to the dot's data-c value, which the section's stylesheet paints. */
const DOT = /** @type {Record<string, string>} */ ({ Red: 'red', White: 'white', Rosé: 'rose' });

class ClubResult extends Component {
  requiredRefs = [
    'profiles', 'reveal', 'profileName', 'why', 'fan', 'factSplit', 'factPrice', 'factSwaps',
    'widened', 'widenedLead', 'named', 'namedTemplate', 'seeCase', 'revealRestart', 'resultView',
    'resultEyebrow', 'resultHeading', 'resultIntro', 'shortNote', 'shortRestart', 'caseList', 'restart', 'startAgain',
    'sizeInputs', 'sizeSaves', 'sizeRates', 'eachPrices', 'sizeWhys', 'compare', 'compareLead', 'compareRest',
    'cadenceInputs', 'fit', 'total', 'panel', 'pay', 'due', 'offReason', 'cut',
    'nextTodayRow', 'nextToday', 'nextEvery', 'subscribe',
    'buyOnce', 'notice', 'sticky', 'stickyPrice', 'stickyCadence', 'stickyDetail',
    'stickySubscribe', 'announce', 'bottleTemplate',
    'drawer', 'drawerHeading', 'drawerCurrent', 'drawerClose', 'colourChip', 'bodyChip',
    'countryFilter', 'priceFilter', 'candidateList', 'drawerEmpty', 'drawerNote', 'candidateTemplate',
    'replacedNote', 'about', 'aboutHeading', 'aboutClose', 'aboutStatus', 'aboutBody', 'aboutError',
    'aboutRetry', 'aboutErrorLink', 'aboutSwap', 'aboutLink',
  ];

  /** @type {Wine[]} */ #pool = [];
  /** @type {Wine[]} */ #bottles = [];
  /** @type {Answers} */ #answers = {};
  /** @type {number} */ #size = DEFAULT_CASE_SIZE;
  /** @type {number} */ #cadence = 2;
  /** @type {{ rank: number, unit: string, count: number }[]} the plans on offer, read from Loop */ #plans = [];
  /** @type {{ size: number, discount: number }[]} */ #tiers = [];
  /** @type {Record<string, string>} */ #profiles = {};
  /** @type {string} */ #profile = '';
  /** @type {Set<number>} tiers already reported, so club_tier_unlocked fires once each */
  #unlocked = new Set();
  /** @type {Record<string, string> | null} */ #stringCache = null;
  #adapter = createAdapter();
  /** @type {boolean} an order is on its way to the cart, so the buttons are off */
  #placing = false;
  /** @type {'subscribe' | 'once' | null} which way of buying was pressed, so that button says so */
  #pressed = null;
  /** @type {{ subscribe: string, once: string }} the order buttons' words, as last worked out */
  #labels = { subscribe: '', once: '' };
  /** @type {string} club-case.js `answersKey` of the answers the case was built from */
  #builtFrom = '';
  /** @type {number | null} how many wines the pool had for the size the quiz asked for, when it could not fill it */
  #cut = null;
  /** @type {ResizeObserver | null} keeps --cl-pay-h and --cl-panel-h, the order block's and the panel's heights, current */
  #payObserver = null;
  /** @type {{ size: number, cadence: number } | null} what the quiz's pace answer asked for, if it asked */
  #told = null;
  /** @type {Wine[]} bottles seven to twelve as the customer last had them, kept while the case is six */
  #tail = [];
  /** @type {Set<Wine['id']>} wines the customer swapped out, so growing the case does not bring them back */
  #swappedOut = new Set();
  /** @type {{ written: number, generated: number }} which kind of line each bottle showed */
  #whySource = { written: 0, generated: 0 };
  /** @type {string} the line saying which saved bottles were replaced, until the case next changes */
  #replaced = '';
  /** @type {string[]} the quiz's question axes, so a fact is a button only when it has a question to open */
  #axes = [];

  /** @type {number} the slot being swapped, -1 when the drawer is shut */
  #slot = -1;
  /** @type {HTMLElement | null} where focus goes back to */
  #opener = null;
  /** @type {boolean} club_picker_opened fires once per result */
  #pickerOpened = false;
  /** club_result_viewed (and club_why_source) are sent once per built result, on any route to it. */
  #resultViewed = false;
  /** @type {Wine[]} the quiz's ranking with colour and price lifted, so the drawer can offer anything */
  #ranked = [];
  /** @type {((wine: Wine) => boolean) | undefined} in the shopper's price band; unset for Mix it up */
  #inBand;
  /** @type {{ colour: boolean, body: boolean, country: string, maxPrice: number | null }} */
  #filters = { colour: true, body: true, country: '', maxPrice: null };

  /** @type {number} the slot the details sheet is about, -1 when it is shut */
  #aboutSlot = -1;
  /** @type {HTMLElement | null} the card button that opened the sheet, where focus goes back to */
  #aboutOpener = null;
  /** @type {Map<string, DocumentFragment>} each wine's fetched details, by handle, for the visit */
  #aboutCache = new Map();
  /** @type {number} bumped at every open, so a slow answer for an earlier wine is not shown */
  #aboutAsk = 0;
  /** @type {boolean} the sheet is closing to open the swap drawer, which takes focus itself */
  #aboutToSwap = false;

  connectedCallback() {
    super.connectedCallback();
    const d = this.dataset;
    this.#adapter = createAdapter({
      route: d.route,
      root: d.root || '/',
      settings: {
        discount6: Number(d.discountSix),
        discount12: Number(d.discountTwelve),
        defaultCadence: Number(d.defaultCadence),
        bundleId: d.bundleId,
      },
    });
    // Loop's bundle took 0.7s to read (measured 2026-09-30), which was a blank wait between the
    // last answer and the reveal. Asked for on connect, it is there by the time the quiz ends. A
    // failure here is silent on purpose: #details() asks again and reports it.
    this.#adapter.details().catch(() => {});
    try {
      this.#profiles = JSON.parse(this.refs.profiles.textContent ?? '{}');
    } catch {
      this.#profiles = {};
    }
    this.#applyTexts();

    document.addEventListener('wine-club:complete', this.#onComplete);
    document.addEventListener('wine-club:back', this.#onBack);
    document.addEventListener('wine-club:hide', this.#onHide);
    this.refs.seeCase.addEventListener('click', this.#onSeeCase);
    this.refs.reveal.addEventListener('click', this.#onEditFact);
    this.refs.restart.addEventListener('click', this.#onRestart);
    this.refs.revealRestart.addEventListener('click', this.#onRestart);
    this.refs.shortRestart.addEventListener('click', this.#onRestart);
    for (const button of this.refs.startAgain) button.addEventListener('click', this.#onStartAgain);
    this.addEventListener('change', this.#onChange);
    this.refs.subscribe.addEventListener('click', this.#onSubscribe);
    this.refs.stickySubscribe.addEventListener('click', this.#onSubscribe);
    this.refs.buyOnce.addEventListener('click', this.#onBuyOnce);
    this.refs.caseList.addEventListener('click', this.#onSwapClick);
    this.refs.drawerClose.addEventListener('click', this.#closeDrawer);
    this.refs.drawer.addEventListener('close', this.#onDrawerClosed);
    this.refs.drawer.addEventListener('click', this.#onDrawerClick);
    this.refs.colourChip.addEventListener('click', this.#onChip);
    this.refs.bodyChip.addEventListener('click', this.#onChip);
    this.refs.countryFilter.addEventListener('change', this.#onSelect);
    this.refs.priceFilter.addEventListener('change', this.#onSelect);
    this.refs.candidateList.addEventListener('click', this.#onPick);
    this.refs.aboutClose.addEventListener('click', this.#closeAbout);
    this.refs.about.addEventListener('close', this.#onAboutClosed);
    this.refs.about.addEventListener('click', this.#onDrawerClick);
    this.refs.aboutRetry.addEventListener('click', this.#onAboutRetry);
    this.refs.aboutSwap.addEventListener('click', this.#onAboutSwap);
    // The pinned order block's height, and the panel's: the stylesheet lets a panel taller than
    // the screen run past its top until its foot is in view (GV-28), which needs the height.
    if (typeof ResizeObserver === 'function') {
      this.#payObserver = new ResizeObserver(() => {
        this.style.setProperty('--cl-pay-h', `${Math.ceil(this.refs.pay.offsetHeight)}px`);
        this.style.setProperty('--cl-panel-h', `${Math.ceil(this.refs.panel.offsetHeight)}px`);
      });
      this.#payObserver.observe(this.refs.pay);
      this.#payObserver.observe(this.refs.panel);
    }
    this.refs.panel.addEventListener('focusin', this.#onPanelFocus);
  }

  disconnectedCallback() {
    document.removeEventListener('wine-club:complete', this.#onComplete);
    document.removeEventListener('wine-club:back', this.#onBack);
    document.removeEventListener('wine-club:hide', this.#onHide);
    this.refs.seeCase.removeEventListener('click', this.#onSeeCase);
    this.refs.reveal.removeEventListener('click', this.#onEditFact);
    this.refs.restart.removeEventListener('click', this.#onRestart);
    this.refs.revealRestart.removeEventListener('click', this.#onRestart);
    this.refs.shortRestart.removeEventListener('click', this.#onRestart);
    for (const button of this.refs.startAgain) button.removeEventListener('click', this.#onStartAgain);
    this.removeEventListener('change', this.#onChange);
    this.refs.subscribe.removeEventListener('click', this.#onSubscribe);
    this.refs.stickySubscribe.removeEventListener('click', this.#onSubscribe);
    this.refs.buyOnce.removeEventListener('click', this.#onBuyOnce);
    this.refs.caseList.removeEventListener('click', this.#onSwapClick);
    this.refs.drawerClose.removeEventListener('click', this.#closeDrawer);
    this.refs.drawer.removeEventListener('close', this.#onDrawerClosed);
    this.refs.drawer.removeEventListener('click', this.#onDrawerClick);
    this.refs.colourChip.removeEventListener('click', this.#onChip);
    this.refs.bodyChip.removeEventListener('click', this.#onChip);
    this.refs.countryFilter.removeEventListener('change', this.#onSelect);
    this.refs.priceFilter.removeEventListener('change', this.#onSelect);
    this.refs.candidateList.removeEventListener('click', this.#onPick);
    this.refs.aboutClose.removeEventListener('click', this.#closeAbout);
    this.refs.about.removeEventListener('close', this.#onAboutClosed);
    this.refs.about.removeEventListener('click', this.#onDrawerClick);
    this.refs.aboutRetry.removeEventListener('click', this.#onAboutRetry);
    this.refs.aboutSwap.removeEventListener('click', this.#onAboutSwap);
    this.#payObserver?.disconnect();
    this.refs.panel.removeEventListener('focusin', this.#onPanelFocus);
    super.disconnectedCallback();
  }

  /** @param {Event} event */
  #onComplete = async (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail ?? {};
    const { profile, wines, pool } = detail;
    const view = detail.view === 'case' ? 'case' : 'reveal';
    this.#axes = Array.isArray(detail.axes) ? detail.axes.filter((axis) => typeof axis === 'string') : [];
    // Back from the reveal and Continue sends the answers again. Unchanged, the case the customer
    // has been working on is still the answer to them: rebuilding it dropped every swap, the size
    // and the frequency. The pool's length is in the key so a different pool is a different case.
    const from = `${answersKey(profile ?? {}, detail)}#${Array.isArray(pool) ? pool.length : 0}`;
    if (from === this.#builtFrom && this.#bottles.length) {
      this.#open(view);
      return;
    }
    this.#builtFrom = '';
    this.#answers = profile ?? {};
    this.#pool = Array.isArray(pool) ? pool : [];
    this.#bottles = Array.isArray(wines) ? wines : [];
    this.#told = this.#pace(detail);
    this.#size = this.#told?.size ?? DEFAULT_CASE_SIZE;
    this.#tail = [];
    this.#swappedOut.clear();
    this.#profile = profileKey(this.#answers);
    this.#unlocked.clear();
    this.refs.notice.textContent = '';
    // The drawer ranks the whole pool by taste, and orders it by price band: the shopper's own
    // band first, then outward (club-case.js `byBand`). `bands` is every price answer the quiz has.
    const { colour: _colour, price: _price, ...taste } = this.#answers;
    const bands = Array.isArray(detail.bands) ? detail.bands.filter((b) => typeof b === 'string') : [];
    this.#ranked = byBand(rank(this.#pool, taste), this.#answers.price, bands);
    const step = bandStep(this.#answers.price, bands);
    this.#inBand = step ? (wine) => step(wine) === 0 : undefined;
    this.#pickerOpened = false;
    this.#resultViewed = false;

    const details = await this.#details();
    this.#keepBuyable(details.variantIds);
    // A size the pool cannot fill is not opened on: nine rosés made a "case of twelve" holding
    // nine that could not be bought. openingSize() steps down to a size that can be filled, and
    // #cut keeps how many wines there were, for the reveal's line and the other size's face.
    const fill = (/** @type {number} */ size) => recommend(this.#pool, this.#answers, size).length;
    const open = openingSize(this.#size, fill);
    this.#cut = open.cut ? fill(this.#size) : null;
    this.#size = open.size;
    // The quiz builds its case at its own size. When it also says which size this household
    // wants, the case is brought to it here, the way a change of size on the screen would.
    if ((this.#told || open.cut) && this.#bottles.length !== this.#size) {
      this.#bottles = resize(this.#bottles, this.#size, recommend(this.#pool, this.#answers, this.#size));
    }
    this.#fillFilterOptions();
    this.#tiers = details.sizes;
    this.#plans = details.cadences;
    this.#cadence = this.#told?.cadence ?? details.defaultCadence;
    this.#replaced = '';
    // A case saved from these same answers is theirs: it comes back, swaps and all.
    const saved = readCase();
    if (saved && saved.answersKey === this.#caseKey()) await this.#restore(saved, Array.isArray(pool) ? pool : []);
    // A rank Loop does not offer (two plans, not three) cannot be bought: open on the default.
    if (!this.#plans.some((plan) => plan.rank === this.#cadence)) this.#cadence = details.defaultCadence;
    this.#labelCadences();
    this.refs.cadenceInputs.forEach((input) => { input.checked = Number(input.value) === this.#cadence; });

    this.#builtFrom = from;
    this.#saveCase();
    this.#open(view);
  };

  /**
   * Put a screen up once the case is built. The case screen is drawn fresh, as See my case draws it.
   * @param {'reveal' | 'case'} view
   */
  #open(view) {
    if (view === 'case') {
      this.#renderCase();
      this.#renderTotals();
    } else {
      this.#renderReveal();
    }
    this.#setView(view);
    // A returning visitor resumes, or Back/Forward lands, straight on the case: that is a result
    // viewed as much as See my case is. Until 2026-10-05 only See my case sent it (GV-38 audit).
    if (view === 'case') this.#trackResultViewed();
  }

  /** The result is on screen: sent once per built result, whichever route brought it up. */
  #trackResultViewed() {
    if (this.#resultViewed) return;
    this.#resultViewed = true;
    const split = colourSplit(this.#bottles);
    track('club_result_viewed', {
      profile: this.#profile, size: this.#size, ...moneyFields(price(this.#bottles, this.#discount()).club),
      reds: split.reds, whites: split.whites, roses: split.roses,
    });
    // How many bottles carried the buyers' own line and how many a generated one, as first shown.
    track('club_why_source', { written: this.#whySource.written, generated: this.#whySource.generated });
  }

  /** The answers the case is built from, as the saved case names them: the quiz's, with no seed. */
  #caseKey() {
    const { seed: _seed, ...asked } = this.#answers;
    return answersKey(asked);
  }

  /** Save the case as it stands (assets/club-case-store.js). Nothing to save until there is one. */
  #saveCase() {
    if (!this.#bottles.length) return;
    writeCase({
      answersKey: this.#caseKey(),
      size: this.#size,
      cadence: String(this.#cadence),
      lines: this.#bottles.map((wine) => ({ variantId: Number(wine.variant_id), handle: handleOf(wine) })),
    });
  }

  /**
   * The saved case, back in place of the one just built. A bottle that has left the club or sold
   * out is replaced with the first wine the swap drawer would offer for its slot, in its own
   * colour when the full pool still says what that was, and the line on the case names both.
   * @param {NonNullable<ReturnType<typeof readCase>>} saved
   * @param {Wine[]} everything the pool as the quiz sent it, before Loop's list narrowed it
   */
  async #restore(saved, everything) {
    const { bottles, replaced } = restoreCase(saved.lines, this.#pool, (bottlesNow, slot, gone) => {
      const was = everything.find((wine) => wine.variant_id === gone.variantId);
      const ranked = was?.colour ? this.#ranked : rank(this.#pool, this.#answers);
      return candidates({ bottles: bottlesNow, slot, ranked, filters: { colour: was?.colour ?? null } })[0];
    });
    if (!bottles.length) return;
    this.#bottles = bottles;
    if (SIZES.includes(saved.size)) this.#size = saved.size;
    const cadence = Number(saved.cadence);
    if ([1, 2, 3].includes(cadence)) this.#cadence = cadence;
    this.#cut = null;
    this.#tail = [];
    const lines = await Promise.all(replaced.map(async ({ gone, wine }) => {
      const out = everything.find((w) => w.variant_id === gone.variantId)?.title ?? await titleOf(this.dataset.root || '/', gone.handle);
      return out
        ? this.#t('replaced', { out, in: wine.title ?? '' })
        : this.#t('replaced_unnamed', { in: wine.title ?? '' });
    }));
    this.#replaced = lines.join(' ');
  }

  /** Back or Forward went from here to one of the quiz's screens. */
  #onHide = () => {
    if (this.refs.drawer.open) this.refs.drawer.close();
    if (this.refs.about.open) this.refs.about.close();
    this.hidden = true;
  };

  /**
   * Put one of the two screens up: show it, make its heading the page's level 1, tell the quiz's
   * bar, start at the top and move focus to the heading.
   * @param {'reveal' | 'case'} view
   */
  #setView(view) {
    const { reveal, resultView, sticky, profileName, resultHeading, drawer, about } = this.refs;
    const onCase = view === 'case';
    // The browser's Back can change the screen with a sheet open over it. The sheet belongs to
    // the case it was opened on, so it goes.
    if (drawer.open) drawer.close();
    if (about.open) about.close();
    this.hidden = false;
    reveal.hidden = onCase;
    resultView.hidden = !onCase;
    sticky.hidden = !onCase;
    const heading = onCase ? resultHeading : profileName;
    (onCase ? profileName : resultHeading).removeAttribute('aria-level');
    heading.setAttribute('aria-level', '1');
    document.dispatchEvent(new CustomEvent('wine-club:view', { detail: { view } }));
    // The section's stylesheet gives it a scroll margin the height of the bar, so its top lands
    // under the bar rather than behind it.
    this.scrollIntoView({ block: 'start' });
    heading.focus({ preventScroll: true });
  }

  /** Back, from the quiz's bar: the case steps back to the reveal, the reveal to the quiz. */
  #onBack = () => {
    if (this.hidden) return;
    if (this.refs.resultView.hidden) {
      this.hidden = true;
      return;
    }
    // The size may have changed and bottles been swapped since the reveal was drawn.
    this.#renderReveal();
    this.#setView('reveal');
  };

  /**
   * The size and frequency the quiz asked for, or null when it asked for neither. paceDefaults()
   * is the one place that knows what a real size and a real frequency are: it answers with its
   * own fallback for anything else, so a pair it hands back unchanged is a pair it accepts.
   * @param {{ size?: unknown, cadence?: unknown }} detail
   * @returns {{ size: number, cadence: number } | null}
   */
  #pace(detail) {
    const asked = { size: Number(detail.size), cadence: Number(detail.cadence) };
    const pace = paceDefaults('quiz', { quiz: asked });
    return pace.size === asked.size && pace.cadence === asked.cadence ? pace : null;
  }

  /**
   * Loop's sizes, discounts and cadences. When Loop cannot be read the page still renders, on the
   * section's placeholder figures, and says nothing is wrong until Subscribe is pressed: place()
   * reads Loop again, so a blip at load costs nothing and an outage fails where the customer acts.
   */
  async #details() {
    try {
      return await this.#adapter.details();
    } catch (error) {
      track('club_error', { stage: 'details', reason: error instanceof Error ? error.message : 'unknown' });
      const d = this.dataset;
      return createAdapter({
        settings: { discount6: Number(d.discountSix), discount12: Number(d.discountTwelve), defaultCadence: Number(d.defaultCadence) },
      }).details();
    }
  }

  /**
   * Loop decides what can go in a case, not the collection. On 2026-09-30 the club collection held
   * 103 wines and Loop's bundle 100, so three wines the quiz could pick would have been refused at
   * Subscribe. The pool is narrowed to Loop's list, and a case holding one of them is rebuilt.
   * @param {number[] | undefined} variantIds absent on the stub, which takes anything
   */
  #keepBuyable(variantIds) {
    if (!variantIds) return;
    const buyable = new Set(variantIds);
    const ok = (/** @type {Wine} */ wine) => buyable.has(/** @type {number} */ (wine.variant_id));
    this.#pool = this.#pool.filter(ok);
    this.#ranked = this.#ranked.filter(ok);
    if (!this.#bottles.every(ok)) this.#bottles = recommend(this.#pool, this.#answers, this.#size);
  }

  #onSeeCase = () => {
    this.#renderCase();
    this.#renderTotals();
    this.#setView('case');
    this.#trackResultViewed();
  };

  #onRestart = () => {
    this.hidden = true;
    document.dispatchEvent(new CustomEvent('wine-club:restart'));
  };

  /**
   * Start again: the quiz forgets the answers and the saved case (wine-club-quiz.js #forget) and
   * opens question 1. The case built here is forgotten as well, so answering the same way again
   * builds a fresh one instead of reopening this one with its swaps. The cart is not touched.
   */
  #onStartAgain = () => {
    if (this.refs.drawer.open) this.refs.drawer.close();
    if (this.refs.about.open) this.refs.about.close();
    this.#builtFrom = '';
    this.#bottles = [];
    this.hidden = true;
    document.dispatchEvent(new CustomEvent('wine-club:start-again'));
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (this.refs.sizeInputs.includes(input)) this.#setSize(Number(input.value));
    if (this.refs.cadenceInputs.includes(input)) {
      this.#cadence = Number(input.value);
      const cadenceLabel = (input.closest('label')?.textContent ?? '').replace(/\s+/g, ' ').trim();
      track('club_cadence_changed', { cadence: this.#cadence, cadence_label: cadenceLabel });
      this.#renderTotals();
      this.#saveCase();
    }
  };

  /** @param {number} to */
  #setSize(to) {
    if (!SIZES.includes(to) || to === this.#size) return;
    const from = this.#size;
    const before = this.#bottles;
    this.#bottles = this.#caseAt(to);
    this.#tail = to < before.length ? before.slice(to) : [];
    this.#size = to;
    track('club_size_changed', { from, to, where: 'result' });
    for (const wine of this.#bottles.filter((w) => !before.includes(w))) this.#trackBottle('club_bottle_added', wine);
    for (const wine of before.filter((w) => !this.#bottles.includes(w))) this.#trackBottle('club_bottle_removed', wine);
    this.#replaced = '';
    this.#renderCase();
    this.#renderTotals();
    this.#saveCase();
  }

  /**
   * The case at `size`, keeping every swap the customer made. Cutting to six keeps the first six.
   * Growing fills from, in order:
   *   1. the six that were cut, as they were left (#tail), so twelve, six, twelve gives the same
   *      twelve back;
   *   2. the quiz's picks at that size;
   *   3. the next best wines in the answered colours, inside the price band and then outside it
   *      as the quiz itself widens, of a colour the customer swapped out first, so the case
   *      keeps its split;
   *   4. and only if the pool has nothing else, a wine the customer swapped out. Without step 3
   *      a swapped-out bottle always came back in a bigger case, because the quiz's twelve is
   *      exactly twelve and one of them was already refused.
   *
   * The build plan said to carry swaps by slot and re-apply them. Tried in a harness, that put a
   * swapped-out bottle back and shifted the five after it, so the slot no longer held what the
   * swap was made against. Remembering the bottles themselves has no slot to go stale.
   * @param {number} size
   * @returns {Wine[]}
   */
  #caseAt(size) {
    if (size === this.#size) return this.#bottles;
    const gone = (/** @type {Wine} */ wine) => this.#swappedOut.has(wine.id);
    const picks = recommend(this.#pool, this.#answers, size);
    const back = picks.filter(gone);
    const colours = new Set(back.map((wine) => wine.colour));
    const { price: _band, ...anyPrice } = this.#answers;
    const spare = back.length
      ? [...new Set([...rank(this.#pool, this.#answers), ...rank(this.#pool, anyPrice)])].filter((wine) => !gone(wine))
      : [];
    return resize(this.#bottles, size, [
      ...this.#tail,
      ...picks.filter((wine) => !gone(wine)),
      ...spare.filter((wine) => colours.has(wine.colour)),
      ...spare.filter((wine) => !colours.has(wine.colour)),
      ...back,
    ]);
  }

  /** @param {number} [size] @returns {number} the discount at a size, the current one by default */
  #discount(size = this.#size) {
    return this.#tiers.find((t) => t.size === size)?.discount ?? 0;
  }

  /** The delivery terms the section was rendered with, in pence. A blank one is null: not stated. */
  #terms() {
    const pence = (/** @type {string | undefined} */ raw) => (raw === undefined || raw.trim() === '' ? null : Number(raw));
    return {
      threshold: pence(this.dataset.freeOver),
      charge: pence(this.dataset.deliveryCharge),
      firstFree: this.dataset.firstFree === 'true',
    };
  }

  /**
   * The profile's name, fitted to the colours the case holds (club-case.js `profileName`). A cell
   * left blank in the editor is the Taste profile panel's stand-in, never an empty heading: the
   * section writes that stand-in into the names too, and this holds if a name arrives blank anyway.
   */
  #profileLabel() {
    const name = profileName(this.#profiles[this.#profile], colourSplit(this.#bottles), {
      red: this.#t('why_colours_red'), white: this.#t('why_colours_white'), rose: this.#t('why_colours_rose'),
    });
    return name.trim() ? name : this.#t('profile_default');
  }

  /** Pairing handles to the words for them: every `food_<handle>` string the section rendered. */
  #foodNames() {
    /** @type {Record<string, string>} */
    const names = {};
    for (const [key, text] of Object.entries(this.#strings())) {
      if (key.startsWith('food_')) names[key.slice(5)] = text;
    }
    return names;
  }

  /**
   * Show a flag beside a country, or leave the image hidden when the wine has none.
   * @param {ParentNode} node @param {Wine} wine
   */
  #flag(node, wine) {
    const img = node.querySelector('[data-field="flag"]');
    if (!(img instanceof HTMLImageElement) || !wine.flag) return;
    img.src = wine.flag;
    img.hidden = false;
  }

  /** @param {number} pence */
  #money(pence) {
    return formatMoney(pence, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'GBP');
  }

  /** @param {string} text */
  #announce(text) {
    this.refs.announce.textContent = '';
    requestAnimationFrame(() => { this.refs.announce.textContent = text; });
  }

  /**
   * Strings the component composes at runtime, rendered server side into the section (Step 5).
   * More than one node carries them: the section's own, for the strings that stay in the locale,
   * and one per editor panel (the four static blocks in sections/club-result.liquid), each with
   * the merchant's words or the locale's. Merged in document order; a node that does not parse is
   * skipped rather than taking the others with it.
   */
  #strings() {
    if (!this.#stringCache) {
      /** @type {Record<string, string>} */
      const merged = {};
      for (const node of this.querySelectorAll('[data-club-strings]')) {
        try {
          Object.assign(merged, JSON.parse(node.textContent ?? '{}'));
        } catch {
          // One panel's words missing is that panel's labels staying as the server wrote them.
        }
      }
      this.#stringCache = merged;
    }
    return /** @type {Record<string, string>} */ (this.#stringCache);
  }

  /**
   * The words the section's markup was rendered with, swapped for the editor's where a panel set
   * them. The section cannot read its own static blocks' settings, so the markup carries the
   * locale's words and names the string that may replace them (`data-club-text`). The templates
   * are included, so a card or a candidate is cloned with the right words already in it. The
   * section is hidden until the quiz ends, so this is done long before anything is on screen.
   */
  #applyTexts() {
    const roots = [this, ...[...this.querySelectorAll('template')].map((template) => template.content)];
    for (const root of roots) {
      for (const node of root.querySelectorAll('[data-club-text]')) {
        const text = this.#t(node.getAttribute('data-club-text') ?? '');
        if (text && node.textContent !== text) node.textContent = text;
      }
    }
  }

  /**
   * A plan's name, in one of three forms: `every` ("Every 4 weeks"), `recurring` ("every 4 weeks",
   * inside a sentence) or `short` ("4 weeks", on its radio). Named from Loop's plan, so the day Loop
   * moves the club from months to weeks the page follows with no theme change. The rank's own
   * string stands in when Loop gave no plan in a unit there are words for; a short form has none,
   * and the radio keeps the words it was rendered with.
   * @param {'every' | 'recurring' | 'short'} form @param {number} rank
   */
  #cadenceText(form, rank) {
    const plan = this.#plans.find((p) => p.rank === rank);
    const unit = plan ? { WEEK: 'week', MONTH: 'month' }[plan.unit] : undefined;
    if (plan && unit) return this.#t(`plan_${form}_${unit}${plan.count === 1 ? '' : 's'}`, { count: plan.count });
    if (form === 'short') return '';
    return this.#t(form === 'every' ? `cadence_${rank}` : `total_recurring_${rank}`);
  }

  /**
   * Each frequency radio says its plan, and a rank Loop does not offer is not shown. The editor's
   * own words for a rank (the price panel's "How often" fields) win over the plan's; they are blank
   * unless someone typed them.
   */
  #labelCadences() {
    for (const input of this.refs.cadenceInputs) {
      const rank = Number(input.value);
      const label = input.closest('label');
      if (!label) continue;
      label.hidden = !this.#plans.some((plan) => plan.rank === rank);
      const span = label.querySelector('[data-club-text]');
      const text = this.#t(`freq_${rank}`) || this.#cadenceText('short', rank);
      if (span && text) span.textContent = text;
    }
  }

  /** @param {string} key @param {Record<string, string | number>} [vars] */
  #t(key, vars = {}) {
    const template = this.#strings()[key] ?? '';
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => String(vars[name] ?? ''));
  }

  /** @param {string} tag @param {string} className @param {string} text */
  #el(tag, className, text) {
    const node = document.createElement(tag);
    node.className = className;
    node.textContent = text;
    return node;
  }

  #renderReveal() {
    const {
      profileName: heading, why, fan, factSplit, factPrice, factSwaps, widened, widenedLead, cut, named,
      namedTemplate, seeCase,
    } = this.refs;
    heading.textContent = this.#profileLabel();
    const split = colourSplit(this.#bottles);
    const colours = split.reds && split.whites ? 'mixed'
      : split.whites ? 'white' : split.roses && !split.reds ? 'rose' : 'red';
    const [row, col] = this.#profile.split('_');
    const avg = this.#bottles.length ? price(this.#bottles, 0).shelf / this.#bottles.length : 0;
    const around = wholePounds(this.#money(Math.round(avg / 100) * 100));
    why.textContent = this.#t('why', {
      body: this.#t(`why_body_${row}`),
      colours: this.#t(`why_colours_${colours}`),
      discovery: this.#t(`why_discovery_${col}`),
      price: around,
    });
    // Every bottle of a six; the first six of a twelve, which is what the band has room for.
    fan.replaceChildren(...this.#bottles.slice(0, 6).map((wine) => {
      const img = document.createElement('img');
      img.src = wine.image ?? '';
      img.alt = '';
      img.width = 180;
      img.height = 219;
      return img;
    }));

    const counts = /** @type {[string, number][]} */ ([['red', split.reds], ['white', split.whites], ['rose', split.roses]]);
    this.#fact(factSplit, 'colour', counts.filter(([, n]) => n > 0).map(([key, n]) => this.#t(`fact_${key}`, { count: n })).join(', '));
    this.#fact(factPrice, 'price', this.#bottles.length ? this.#t('fact_price', { price: around }) : '');

    // Under six means scoring widened the price to fill the case (it never widens the colour).
    // The reveal says so in place of the "to swap from" chip. With none fitting there is no count
    // worth stating: "Only 0 wines fit" says less than that nothing is at that price.
    const fits = fitting(this.#pool, this.#answers);
    // The chip counts what the swap drawer lists (club-case.js `swapCount`), not what fits the
    // answers: it said 9 while the drawer offered 24.
    const swaps = swapCount(this.#bottles, this.#ranked, Number(this.dataset.showstopperMin) || 0);
    factSwaps.textContent = this.#t('fact_swaps', { count: swaps });
    factSwaps.hidden = fits < 6 || swaps === 0;
    widenedLead.textContent = this.#t(fits === 0 ? 'widened_none' : fits === 1 ? 'widened_one' : 'widened_other', { count: fits });
    widened.hidden = fits >= 6;
    // The case opened on a smaller size than the pace answer asked for, and why.
    cut.hidden = this.#cut === null;
    cut.textContent = this.#cut === null ? '' : this.#t('cut_note', { count: this.#cut });

    named.setAttribute('aria-label', this.#t(`named_label_${this.#size}`));
    named.replaceChildren(...this.#bottles.map((wine) => {
      const node = /** @type {DocumentFragment} */ (namedTemplate.content.cloneNode(true));
      const set = (/** @type {string} */ field, /** @type {string} */ text) => {
        const el = node.querySelector(`[data-field="${field}"]`);
        if (el) el.textContent = text;
      };
      const dot = node.querySelector('[data-field="dot"]');
      if (dot instanceof HTMLElement) dot.dataset.c = DOT[wine.colour ?? ''] ?? '';
      set('title', wine.title ?? '');
      set('colour', wine.colour ? `, ${wine.colour}` : '');
      set('country', wine.country ?? '');
      this.#flag(node, wine);
      return node;
    }));
    const cta = this.#t(`reveal_cta_${this.#size}`);
    if (cta) seeCase.textContent = cta;
  }

  /**
   * One of the two facts that can change an answer: its words on the button, named for what it does
   * ("Change colour: 6 red"), or on its plain twin when the quiz has no question for `axis`.
   * @param {HTMLElement} item the fact's list item @param {string} axis @param {string} text
   */
  #fact(item, axis, text) {
    item.hidden = !text;
    const button = item.querySelector('[data-edit]');
    const plain = item.querySelector('[data-fact-plain]');
    const editable = this.#axes.includes(axis);
    if (button instanceof HTMLButtonElement) {
      button.hidden = !editable;
      const words = button.querySelector('[data-fact-text]');
      if (words) words.textContent = text;
      button.setAttribute('aria-label', this.#t(`fact_edit_${axis}`, { value: text }) || text);
    }
    if (plain instanceof HTMLElement) {
      plain.hidden = editable;
      plain.textContent = text;
    }
  }

  /**
   * A colour or price fact pressed: the quiz opens that question, answers kept (wine-club:edit).
   * @param {Event} event
   */
  #onEditFact = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-edit]') : null;
    if (!(button instanceof HTMLElement) || button.hidden) return;
    this.hidden = true;
    document.dispatchEvent(new CustomEvent('wine-club:edit', { detail: { axis: button.dataset.edit } }));
  };

  /**
   * The sentence for a generated reason. reason() says which kind; the words are locale strings.
   * A food is named in words, never as the pool's handle for it (club-case.js `foodName`). With
   * no reason there is no line: it used to restate the colour and country ("Red, from Spain.")
   * straight above the row that gives them, and the card's rows now carry the grape as well.
   * @param {ReturnType<typeof reason>} why
   */
  #line(why) {
    if (why.kind === 'food') {
      const names = this.#foodNames();
      const [first, second] = why.foods.map((food) => foodName(food, names));
      return this.#t('line_food', { foods: second ? this.#t('line_and', { first: first ?? '', second }) : first ?? '' });
    }
    return why.kind === 'price' ? this.#t('line_price') : '';
  }

  #renderCase() {
    const { caseList, bottleTemplate, shortNote, resultHeading, resultEyebrow, resultIntro } = this.refs;
    const source = { written: 0, generated: 0 };
    // One count of the foods named across this case, so the lines do not all name the same two.
    /** @type {Map<string, number>} */
    const named = new Map();
    caseList.replaceChildren(...this.#bottles.map((wine, slot) => {
      const node = /** @type {DocumentFragment} */ (bottleTemplate.content.cloneNode(true));
      const set = (/** @type {string} */ field, /** @type {string} */ text) => {
        const el = node.querySelector(`[data-field="${field}"]`);
        if (el) el.textContent = text;
        return el;
      };
      const img = node.querySelector('[data-field="image"]');
      if (img instanceof HTMLImageElement) img.src = wine.image ?? '';
      set('title', wine.title ?? '');
      const about = node.querySelector('[data-about]');
      if (about instanceof HTMLElement) {
        about.dataset.about = String(slot);
        about.setAttribute('aria-label', this.#t('about_open', { title: wine.title ?? '' }));
      }

      // The buyers' own line in the note face, signed; failing that a generated one, unsigned.
      const why = reason(wine, this.#answers, named);
      if (why.kind === 'written') {
        set('noteText', why.text);
        node.querySelector('[data-field="note"]')?.removeAttribute('hidden');
        source.written += 1;
      } else {
        const text = this.#line(why);
        if (text) {
          set('why', text)?.removeAttribute('hidden');
          source.generated += 1;
        }
      }

      // The shelf card's facts. Each is shown only when the wine has it; a strength of "0" is one.
      const show = (/** @type {string} */ row) => node.querySelector(`[data-field="${row}"]`)?.removeAttribute('hidden');
      if (wine.abv != null && wine.abv !== '') set('abv', this.#t('abv', { abv: wine.abv }))?.removeAttribute('hidden');
      if (wine.country) {
        set('country', wine.country);
        show('countryRow');
        this.#flag(node, wine);
      }
      if (wine.grapes?.length) {
        set('grapes', wine.grapes.join(', '));
        show('grapeRow');
      }
      if (wine.colour) {
        set('colour', wine.colour);
        show('colourRow');
      }
      if (wine.sweetness) {
        set('sweetness', wine.sweetness);
        show('sweetRow');
      }
      set('price', this.#money(wine.price ?? 0));
      set('swapName', `, ${wine.title ?? ''}`);
      const button = node.querySelector('[data-slot]');
      if (button instanceof HTMLElement) button.dataset.slot = String(slot);
      return node;
    }));
    this.#whySource = source;
    shortNote.hidden = this.#bottles.length >= this.#size;
    this.refs.replacedNote.textContent = this.#replaced;
    this.refs.replacedNote.hidden = !this.#replaced;
    resultEyebrow.textContent = this.#t('result_eyebrow', { profile: this.#profileLabel() });
    resultHeading.textContent = this.#t('result_heading', { count: this.#size });
    resultIntro.textContent = this.#t('result_intro', { pool: this.#pool.length });
  }

  #renderTotals() {
    const discount = this.#discount();
    const terms = this.#terms();
    const sum = totals(this.#bottles, discount, terms);
    // Buy once is not the subscription: it pays the ordinary rate, first delivery or not.
    const once = totals(this.#bottles, 0, { threshold: terms.threshold, charge: terms.charge });
    const recurring = this.#cadenceText('recurring', this.#cadence);
    const every = this.#cadenceText('every', this.#cadence);
    const stated = sum.delivery !== null;
    const first = terms.firstFree;
    const short = this.#bottles.length < this.#size;

    // Each face: what that size saves in pounds, the rate, and the price a bottle. A size the
    // pool cannot fill is switched off and says why in place of the figures.
    this.refs.sizeInputs.forEach((input, i) => {
      const size = Number(input.value);
      input.checked = size === this.#size;
      const bottles = this.#caseAt(size);
      const can = size === this.#size || bottles.length >= size;
      input.disabled = !can;
      const rate = this.#discount(size);
      const at = price(bottles, rate);
      const fill = (/** @type {HTMLElement | undefined} */ slot, /** @type {string} */ text) => {
        if (!slot) return;
        slot.textContent = text;
        slot.hidden = !text;
      };
      fill(this.refs.sizeSaves[i], can && rate ? this.#t('size_save', { price: this.#money(at.saved) }) : '');
      fill(this.refs.sizeRates[i], can && rate ? this.#t('size_rate', { discount: rate }) : '');
      fill(this.refs.eachPrices[i], can ? this.#t('each', { price: this.#money(at.each) }) : '');
      const why = this.refs.sizeWhys[i];
      fill(why, can ? '' : this.#t('size_short', { count: bottles.length }));
      if (why && !can) input.setAttribute('aria-describedby', why.id);
      else input.removeAttribute('aria-describedby');
    });

    // What twelve saves over six, on the SAME twelve bottles at both rates (compareSizes takes
    // one case so the other comparison cannot be written). Left out when there is nothing true
    // to say: a tier missing, a twelve the pool could not fill, or no extra saving.
    const six = this.#tiers.find((t) => t.size === 6);
    const twelve = this.#tiers.find((t) => t.size === 12);
    const dozen = this.#caseAt(12);
    const more = six && twelve && dozen.length === 12 ? compareSizes(dozen, six.discount, twelve.discount).more : 0;
    this.refs.compare.hidden = more <= 0;
    if (six && twelve && more > 0) {
      const at = this.#size === 12 ? 'twelve' : 'six';
      this.refs.compareLead.textContent = this.#t(`compare_lead_${at}`, { discount: twelve.discount, current: six.discount });
      this.refs.compareRest.textContent = this.#t(`compare_rest_${at}`, { price: this.#money(more) });
    }
    if (this.#size === 12 && twelve && !this.#unlocked.has(12)) {
      this.#unlocked.add(12);
      track('club_tier_unlocked', { tier: 12, discount: twelve.discount });
    }

    // Whether the frequency is the one their answer pointed at. Hidden when the quiz did not say.
    const told = this.#told?.cadence;
    this.refs.fit.hidden = !told;
    if (told) {
      this.refs.fit.textContent = told === this.#cadence
        ? this.#t('fit_yes')
        : this.#t('fit_no', { recurring: this.#cadenceText('recurring', told) });
    }

    const row = (/** @type {string} */ className, /** @type {string} */ label, /** @type {string} */ value, valueClass = '') => {
      const el = this.#el('div', `${className} gv-body-s`.trim(), '');
      el.append(this.#el('span', '', label), this.#el('span', valueClass, value));
      return el;
    };
    const rows = [row('cl-total__row', this.#t('row_shelf', { count: this.#bottles.length }), this.#money(sum.shelf))];
    if (discount > 0) rows.push(row('cl-total__row cl-total__row--save', this.#t('row_saving', { discount }), `−${this.#money(sum.saved)}`));
    // DELIVERY IS PRINTED ONLY WHEN THE SECTION STATES IT. With the first delivery free that is
    // what the row says. Otherwise totals() answers null until both the threshold and the charge
    // are set, and then there is no delivery row, no "today", and the total is the club price.
    // Zero is free, and is said as a word.
    if (first) {
      rows.push(row('cl-total__row', this.#t('row_delivery'), this.#t('delivery_first_free')));
    } else if (stated) {
      rows.push(sum.delivery
        ? row('cl-total__row', this.#t('row_delivery_under', { price: wholePounds(this.#money(terms.threshold ?? 0)) }), this.#money(sum.delivery))
        : row('cl-total__row', this.#t('row_delivery'), this.#t('delivery_free')));
    }
    this.refs.total.replaceChildren(...rows);

    // What is charged today, and with a free first delivery one line for every case after it:
    // the club price, and what its delivery will cost. That is the whole of the rule.
    const label = first ? this.#t('today') : stated ? this.#t('today_then', { recurring }) : every;
    const due = [row('cl-due__row', label, this.#money(sum.today), 'gv-h2 price')];
    if (first) {
      const then = { price: this.#money(sum.club), recurring, delivery: this.#money(sum.later ?? 0) };
      const key = sum.later === null ? 'then' : sum.later === 0 ? 'then_free' : 'then_plus';
      due.push(this.#el('p', 'cl-due__then gv-body-s', this.#t(key, then)));
    }
    this.refs.due.replaceChildren(...due);

    this.#labels = {
      subscribe: this.#t('subscribe', { price: this.#money(sum.today) }),
      once: once.delivery
        ? this.#t('buy_once_delivery', { price: this.#money(once.today), delivery: this.#money(once.delivery) })
        : this.#t('buy_once', { price: this.#money(once.today) }),
    };
    this.#setButtons();

    this.refs.nextTodayRow.hidden = !stated;
    this.refs.nextToday.textContent = this.#t('next_today', { price: this.#money(sum.today) });
    this.refs.nextEvery.textContent = every;

    // The pinned bar: the amount, then "today" when that is what it is, the frequency when the
    // same is charged every time. A short case says why the button beside it is off.
    this.refs.sticky.classList.toggle('cl-sticky--off', short);
    this.refs.stickyPrice.textContent = this.#money(sum.today);
    this.refs.stickyCadence.textContent = first ? this.#t('sticky_today') : recurring;
    this.refs.stickyDetail.textContent = short
      ? this.#t('short_reason')
      : discount > 0
        ? this.#t('sticky_detail', { count: this.#bottles.length, price: this.#money(sum.saved), discount })
        : this.#t('size_option', { count: this.#bottles.length });
  }

  /**
   * @param {'club_bottle_added' | 'club_bottle_removed'} name
   * @param {Wine} wine
   * @param {boolean} [showstopper]
   */
  #trackBottle(name, wine, showstopper = false) {
    const payload = {
      product_id: wine.id, title: wine.title, colour: wine.colour, body: wine.body,
      country: wine.country, price: wine.price, showstopper,
    };
    // Literal calls, not track(name, payload): scripts/check-club-events.mjs matches a track call
    // by its literal quoted event name as text, so a name passed through a variable is invisible
    // to it and a live event would look unsent. Same behaviour, statically checkable.
    if (name === 'club_bottle_added') track('club_bottle_added', payload);
    else if (name === 'club_bottle_removed') track('club_bottle_removed', payload);
  }

  /**
   * The three order buttons are off while a case is short, and while one is on its way to the
   * cart. Neither is silent. A short case shows the reason by the buttons and points each of them
   * at it. An order on its way changes the words on the button that was pressed: the state was
   * half opacity and an unchanged label.
   */
  #setButtons() {
    const { subscribe, stickySubscribe, buyOnce, offReason, resultView } = this.refs;
    const short = this.#bottles.length < this.#size;
    const off = this.#placing || short;
    offReason.hidden = !short;
    for (const button of [subscribe, stickySubscribe, buyOnce]) {
      button.disabled = off;
      if (short) button.setAttribute('aria-describedby', offReason.id);
      else button.removeAttribute('aria-describedby');
    }
    const working = this.#placing ? this.#t('placing') : '';
    subscribe.textContent = working && this.#pressed === 'subscribe' ? working : this.#labels.subscribe;
    stickySubscribe.textContent = working && this.#pressed === 'subscribe' ? working : this.#t('subscribe_short');
    buyOnce.textContent = working && this.#pressed === 'once' ? working : this.#labels.once;
    resultView.toggleAttribute('aria-busy', this.#placing);
  }

  /**
   * Say what happened to an order where the customer is looking. The notice sits by the panel's
   * buttons; pressed from the pinned bar at the top of a phone page it was over a screen away.
   * @param {string} text
   */
  #notify(text) {
    const { notice } = this.refs;
    notice.textContent = text;
    notice.scrollIntoView({ block: 'center' });
    notice.focus({ preventScroll: true });
  }

  /** @param {number | null} cadence a plan's rank, 1 shortest to 3 longest, or null to buy once */
  async #place(cadence) {
    if (this.#placing) return;
    this.#placing = true;
    this.#pressed = cadence === null ? 'once' : 'subscribe';
    this.#setButtons();
    this.refs.notice.textContent = '';
    const discount = cadence === null ? 0 : this.#discount();
    const expected = price(this.#bottles, discount).club;
    /** @type {import('./club-bundle-adapter.js').Outcome} */
    let outcome;
    try {
      outcome = await this.#adapter.place({
        lines: lines(this.#bottles),
        size: this.#size,
        cadence,
        // Carried onto the order, so revenue by entry point reads from Shopify's own orders (spec 6).
        // `_club_colour` is its own attribute, not folded into `_club_profile`: the profile is
        // "body_discovery", and this file splits it on "_" to name the reveal's words.
        attributes: {
          _club_path: 'build', _club_profile: this.#profile, _club_colour: colourKey(this.#answers),
          _club_entry: entryFrom(window.location.search),
        },
      });
    } catch {
      outcome = { ok: false, stage: 'adapter', reason: 'threw' };
    }
    if (!outcome.ok) {
      track('club_error', { stage: outcome.stage, reason: outcome.reason });
      this.#placing = false;
      this.#setButtons();
      this.#notify(this.#t('error_notice'));
      return;
    }
    if ('stub' in outcome) {
      this.#placing = false;
      this.#setButtons();
      this.#notify(this.#t('stub_notice'));
      return;
    }
    // Only a cart holding this case and nothing else can be compared with the figure on the button.
    // Any difference is reported; more than a penny also sends the shopper to the basket rather
    // than checkout, so they see the real total first (club-case.js `landing`).
    if (outcome.cartCount === this.#size && outcome.cartTotal !== expected) {
      track('club_error', { stage: 'price', reason: `shown_${expected}_cart_${outcome.cartTotal}` });
    }
    const sent = trackThen('club_case_added_to_cart', {
      path: 'build', size: this.#size, cadence: cadence ?? 0, ...moneyFields(expected), discount,
      bottles: this.#bottles.length, profile: this.#profile, colour: colourKey(this.#answers),
    });
    // The case is bought: nothing is left to pick up, here or from the rest of the site.
    clearAll();
    // Leaving on the same tick could end the page before the event was out (club-events.js
    // HANDOFF_MS); one held for consent does not wait. The buttons stay off meanwhile, and after:
    // the page is leaving, and a second press would make a second case.
    await sent;
    window.location.assign(landing(outcome, this.#size, expected, this.dataset.root || '/'));
  }

  #onSubscribe = () => { this.#place(this.#cadence); };

  /**
   * Keeps a control the keyboard reaches in the panel in view, from 1200px (WCAG 2.2 2.4.11).
   *
   * THE BROWSER CANNOT DO IT HERE, BECAUSE THE PANEL IS STICKY WITH A NEGATIVE TOP (GV-28): a panel
   * taller than the screen runs past the top until its foot is in view, then holds. Tabbing back
   * up from the bottles to the size choice, the browser scrolled by the distance it measured, the
   * panel stayed where it was held, and the radio ended at -121 to 10px at 1366 by 650, under the
   * bar (measured 2026-10-01). So this scrolls in steps and measures again after each, until the
   * control is clear of the bar above and of the pinned order block below, or nothing moves.
   * @param {FocusEvent} event
   */
  #onPanelFocus = (event) => {
    const control = event.target;
    if (!(control instanceof HTMLElement) || !window.matchMedia('(min-width: 1200px)').matches) return;
    /** @type {HTMLElement | null} */
    let scroller = this.refs.panel.parentElement;
    while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
    const target = scroller ?? document.scrollingElement;
    if (!(target instanceof HTMLElement)) return;
    const root = getComputedStyle(document.documentElement);
    const top = (parseFloat(root.getPropertyValue('--gv-header-h')) || 0) + 16;
    const inPay = this.refs.pay.contains(control);
    for (let step = 0; step < 6; step++) {
      const box = control.getBoundingClientRect();
      const floor = inPay ? window.innerHeight : this.refs.pay.getBoundingClientRect().top;
      let by = 0;
      if (box.top < top) by = box.top - top;
      else if (box.bottom > floor - 8) by = Math.min(box.bottom - floor + 8, box.top - top);
      if (Math.abs(by) < 1) return;
      const was = target.scrollTop;
      target.scrollBy({ top: by, behavior: 'instant' });
      if (target.scrollTop === was) return;
    }
  };

  #onBuyOnce = () => {
    track('club_buy_once_chosen', moneyFields(price(this.#bottles, 0).shelf));
    this.#place(null);
  };

  /** Countries in the pool, and price ceilings at £12, £15 and £20. */
  #fillFilterOptions() {
    const { countryFilter, priceFilter } = this.refs;
    const countries = [...new Set(this.#pool.map((w) => w.country).filter(Boolean))].sort();
    const option = (/** @type {string} */ value, /** @type {string} */ text) => {
      const node = document.createElement('option');
      node.value = value;
      node.textContent = text;
      return node;
    };
    countryFilter.replaceChildren(
      /** @type {HTMLOptionElement} */ (countryFilter.options[0]),
      ...countries.map((c) => option(String(c), String(c))),
    );
    priceFilter.replaceChildren(
      /** @type {HTMLOptionElement} */ (priceFilter.options[0]),
      ...[1200, 1500, 2000].map((p) => option(String(p), this.#t('filter_price_under', { price: wholePounds(this.#money(p)) }))),
    );
  }

  /** @param {Event} event */
  #onSwapClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const about = target.closest('[data-about]');
    if (about instanceof HTMLElement) {
      this.#openAbout(Number(about.dataset.about), about);
      return;
    }
    const button = target.closest('[data-slot]');
    if (!(button instanceof HTMLElement)) return;
    this.#openDrawer(Number(button.dataset.slot), button);
  };

  /** @param {number} slot @param {HTMLElement} opener */
  #openDrawer(slot, opener) {
    const current = this.#bottles[slot];
    if (!current) return;
    this.#slot = slot;
    this.#opener = opener;
    this.#filters = { colour: true, body: Boolean(current.body), country: '', maxPrice: null };
    this.refs.countryFilter.value = '';
    this.refs.priceFilter.value = '';
    // The heading names the wine being swapped out; "bottle 1" made the customer count the cards.
    this.refs.drawerHeading.textContent = this.#t('drawer_heading', { title: current.title ?? '' });
    this.refs.drawerCurrent.textContent = [current.country, current.colour].filter(Boolean).join(' · ');
    this.refs.drawerNote.textContent = this.#t('drawer_note', { discount: this.#discount() });
    this.#renderCandidates();
    this.#markSwapping(slot);
    this.refs.drawer.showModal();
    if (!this.#pickerOpened) {
      this.#pickerOpened = true;
      track('club_picker_opened', { from: 'result' });
    }
  }

  #closeDrawer = () => { this.refs.drawer.close(); };

  /**
   * A press on the dimmed page closes the drawer, or the details sheet. The backdrop is the
   * dialog's own, so the press arrives on the dialog itself; one inside its box is a press on the
   * sheet's own padding.
   * @param {MouseEvent} event
   */
  #onDrawerClick = (event) => {
    const drawer = /** @type {HTMLDialogElement} */ (event.currentTarget);
    if (event.target !== drawer) return;
    const box = drawer.getBoundingClientRect();
    const inside = event.clientX >= box.left && event.clientX <= box.right
      && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) drawer.close();
  };

  #onDrawerClosed = () => {
    this.#slot = -1;
    this.#markSwapping(-1);
    this.#opener?.focus();
  };

  /** @param {number} slot -1 for none */
  #markSwapping(slot) {
    this.refs.caseList.querySelectorAll('.cl-bc').forEach((li, i) => {
      li.classList.toggle('cl-bc--sel', i === slot);
      const label = li.querySelector('[data-field="swap"]');
      if (label) label.textContent = this.#t(i === slot ? 'swapping' : 'swap');
    });
  }

  /** @param {Event} event */
  #onChip = (event) => {
    const chip = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const on = chip.getAttribute('aria-pressed') !== 'true';
    const key = chip === this.refs.colourChip ? 'colour' : 'body';
    this.#filters[key] = on;
    track('club_filter_used', { filter: key, value: on ? 'on' : 'off' });
    this.#renderCandidates();
  };

  /** @param {Event} event */
  #onSelect = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.currentTarget);
    if (select === this.refs.countryFilter) {
      this.#filters.country = select.value;
      track('club_filter_used', { filter: 'country', value: select.value || 'any' });
    } else {
      this.#filters.maxPrice = select.value ? Number(select.value) : null;
      track('club_filter_used', { filter: 'price', value: select.value || 'any' });
    }
    this.#renderCandidates();
  };

  #renderCandidates() {
    const current = this.#bottles[this.#slot];
    if (!current) return;
    const { colourChip, bodyChip, candidateList, candidateTemplate, drawerEmpty } = this.refs;
    colourChip.textContent = this.#t('filter_colour', { colour: current.colour ?? '' });
    colourChip.hidden = !current.colour;
    colourChip.setAttribute('aria-pressed', String(this.#filters.colour));
    bodyChip.textContent = this.#t('filter_body', { body: current.body ?? '' });
    bodyChip.hidden = !current.body;
    bodyChip.setAttribute('aria-pressed', String(this.#filters.body));

    const min = Number(this.dataset.showstopperMin) || 0;
    const list = candidates({
      bottles: this.#bottles,
      slot: this.#slot,
      ranked: this.#ranked,
      showstopperMin: min,
      inBand: this.#inBand,
      filters: {
        colour: this.#filters.colour ? current.colour : null,
        body: this.#filters.body ? current.body : null,
        country: this.#filters.country || null,
        maxPrice: this.#filters.maxPrice,
      },
    });
    const discount = this.#discount();
    // Every candidate, not the first 24: the reveal's "N wines to swap from" is this list's length
    // (club-case.js `swapCount`), and a cut list made that number untrue. The images are lazy.
    // The pinned showstopper is the first one in the list: first, or straight after the wines in
    // the shopper's band (candidates()). Only that one carries the badge.
    const pinned = list.find((wine) => isShowstopper(wine, min));
    candidateList.replaceChildren(...list.map((wine) => {
      const node = /** @type {DocumentFragment} */ (candidateTemplate.content.cloneNode(true));
      const star = wine === pinned;
      const li = node.querySelector('.cl-cand');
      if (li instanceof HTMLElement) {
        li.classList.toggle('cl-cand--star', star);
        li.dataset.id = String(wine.id);
      }
      const set = (/** @type {string} */ field, /** @type {string} */ text) => {
        const el = node.querySelector(`[data-field="${field}"]`);
        if (el) el.textContent = text;
      };
      const img = node.querySelector('[data-field="image"]');
      if (img instanceof HTMLImageElement) img.src = wine.image ?? '';
      const badge = node.querySelector('[data-field="star"]');
      if (badge instanceof HTMLElement) badge.hidden = !star;
      set('title', wine.title ?? '');
      set('facts', [wine.country, this.#money(wine.price ?? 0)].filter(Boolean).join(' · '));
      this.#flag(node, wine);
      // Grape and strength on one line that is cut, not wrapped, when the row is narrow.
      const more = [
        wine.grapes?.slice(0, 2).join(', '),
        wine.abv != null && wine.abv !== '' ? this.#t('abv', { abv: wine.abv }) : '',
      ].filter(Boolean).join(' · ');
      const extra = node.querySelector('[data-field="more"]');
      if (extra instanceof HTMLElement && more) {
        extra.textContent = more;
        extra.hidden = false;
      }
      const d = difference(current, wine, discount);
      set('difference', `${d >= 0 ? '+' : '−'}${this.#money(Math.abs(d))}`);
      set('pickName', `, ${wine.title ?? ''}`);
      return node;
    }));
    drawerEmpty.hidden = list.length > 0;
  }

  /** @param {Event} event */
  #onPick = (event) => {
    const li = /** @type {HTMLElement} */ (event.target).closest('[data-pick]')?.closest('.cl-cand');
    if (!(li instanceof HTMLElement)) return;
    const wine = this.#pool.find((w) => String(w.id) === li.dataset.id);
    const slot = this.#slot;
    const out = this.#bottles[slot];
    if (!wine || !out) return;
    const next = swap(this.#bottles, slot, wine);
    if (next === this.#bottles) return;
    this.#bottles = next;
    this.#swappedOut.add(out.id);
    this.#swappedOut.delete(wine.id);

    const discount = this.#discount();
    const star = isShowstopper(wine, Number(this.dataset.showstopperMin) || 0);
    const used = [
      this.#filters.colour && 'colour', this.#filters.body && 'body',
      this.#filters.country && 'country', this.#filters.maxPrice != null && 'price',
    ].filter(Boolean).join(',');
    track('club_bottle_swapped', { slot: slot + 1, out_id: out.id, in_id: wine.id, filter_used: used || 'none' });
    this.#trackBottle('club_bottle_removed', out);
    this.#trackBottle('club_bottle_added', wine, star);
    if (star) {
      track('club_showstopper_added', {
        product_id: wine.id, title: wine.title, colour: wine.colour, body: wine.body, country: wine.country,
        price: wine.price, showstopper: true, price_delta: difference(out, wine, discount),
      });
    }

    this.refs.drawer.close();
    this.#replaced = '';
    this.#renderCase();
    this.#renderTotals();
    this.#saveCase();
    this.#announce(this.#t('swapped', { out: out.title ?? '', in: wine.title ?? '' }));
    // Focus goes back to the Swap button that opened the drawer. Rendering the case replaced that
    // button, so its successor in the same slot is the opener now. It is focused here, and kept
    // as #opener because the dialog's `close` event arrives after this and focuses #opener too:
    // cleared, as it used to be, that handler had nothing to return focus to.
    const again = this.refs.caseList.querySelector(`[data-slot="${slot}"]`);
    this.#opener = again instanceof HTMLElement ? again : null;
    this.#opener?.focus();
  };

  /**
   * The details sheet for the bottle in `slot`. Its name and the product page's address are known
   * at once, from the pool; the rest is fetched, or taken from what this visit already fetched.
   * @param {number} slot @param {HTMLElement} opener
   */
  #openAbout(slot, opener) {
    const wine = this.#bottles[slot];
    if (!wine) return;
    const { about, aboutHeading, aboutLink, aboutErrorLink } = this.refs;
    this.#aboutSlot = slot;
    this.#aboutOpener = opener;
    aboutHeading.textContent = wine.title ?? '';
    aboutLink.href = wine.url ?? '';
    aboutErrorLink.href = wine.url ?? '';
    about.showModal();
    this.#loadAbout(wine);
  }

  /** @param {Wine} wine */
  async #loadAbout(wine) {
    const { aboutBody, aboutStatus, aboutError } = this.refs;
    const ask = ++this.#aboutAsk;
    const handle = handleOf(wine);
    const kept = this.#aboutCache.get(handle);
    aboutError.hidden = true;
    if (kept) {
      aboutStatus.textContent = '';
      aboutBody.replaceChildren(kept.cloneNode(true));
      aboutBody.scrollTop = 0;
      return;
    }
    aboutBody.replaceChildren();
    aboutBody.setAttribute('aria-busy', 'true');
    aboutStatus.textContent = this.#t('about_loading');
    try {
      const response = await fetch(`${this.dataset.root || '/'}products/${encodeURIComponent(handle)}?section_id=club-wine-details`);
      if (!response.ok) throw new Error(`status_${response.status}`);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = page.querySelector('[data-club-wine-details]');
      if (!content) throw new Error('no_content');
      const kept = document.createDocumentFragment();
      kept.append(document.importNode(settle(content), true));
      this.#aboutCache.set(handle, kept);
      if (ask !== this.#aboutAsk) return;
      aboutStatus.textContent = '';
      aboutBody.replaceChildren(kept.cloneNode(true));
      aboutBody.scrollTop = 0;
    } catch (error) {
      if (ask !== this.#aboutAsk) return;
      track('club_error', { stage: 'details_sheet', reason: error instanceof Error ? error.message : 'unknown' });
      aboutStatus.textContent = this.#t('about_error');
      aboutError.hidden = false;
    } finally {
      if (ask === this.#aboutAsk) aboutBody.removeAttribute('aria-busy');
    }
  }

  #onAboutRetry = () => {
    const wine = this.#bottles[this.#aboutSlot];
    if (wine) this.#loadAbout(wine);
  };

  #closeAbout = () => { this.refs.about.close(); };

  #onAboutClosed = () => {
    this.#aboutAsk += 1;
    this.#aboutSlot = -1;
    if (this.#aboutToSwap) {
      this.#aboutToSwap = false;
      return;
    }
    this.#aboutOpener?.focus();
  };

  /**
   * "Swap this wine": the sheet gives way to the swap drawer for the same slot, which returns
   * focus to that card's Swap button when it closes, as it does when opened from there.
   */
  #onAboutSwap = () => {
    const slot = this.#aboutSlot;
    const swapButton = this.refs.caseList.querySelector(`[data-slot="${slot}"]`);
    if (slot < 0 || !(swapButton instanceof HTMLElement)) return;
    this.#aboutToSwap = true;
    this.refs.about.close();
    this.#openDrawer(slot, swapButton);
  };
}

/**
 * A wine's handle, from the product page address the pool carries.
 * @param {Wine} wine
 */
function handleOf(wine) {
  return decodeURIComponent((wine.url ?? '').split('/products/')[1]?.split(/[?#/]/)[0] ?? '');
}

/**
 * The name of a wine that has left the club, for the line that says it was replaced. It is not in
 * the pool any more, so its product is asked. Null when that fails too: the line then names only
 * the wine that took its place.
 * @param {string} root @param {string} handle
 * @returns {Promise<string | null>}
 */
async function titleOf(root, handle) {
  if (!handle) return null;
  try {
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) return null;
    const product = await response.json();
    return typeof product?.title === 'string' ? product.title : null;
  } catch {
    return null;
  }
}

/**
 * The fetched details, made fit for the sheet before they are shown. The description's pictures
 * go: they are small copies of the bottle already at the top (blocks/product-description.liquid
 * strips them on the product page for the same reason), and the parsed page has not fetched them.
 * The facts' links to their collections become plain text: the sheet is about this wine, and a
 * link away from the case in the middle of it is a way to lose the page.
 * @param {Element} content
 */
function settle(content) {
  content.querySelectorAll('[data-description] img').forEach((img) => img.remove());
  content.querySelectorAll('a.wine-facts__link, a.food-pairing__link').forEach((link) => {
    link.replaceWith(...link.childNodes);
  });
  return content;
}

if (!customElements.get('club-result')) {
  customElements.define('club-result', ClubResult);
}
